import { forEach } from 'lodash';
import { useState } from 'react';
import { PATHS } from 'src/constants/paths';
import {
  fetchViewLocationsByGroupsWithoutPaginationFilterByLocationCode,
  useGetLocationGroup,
  useModal,
  useUpdateLocations,
} from 'src/hooks';
import { useApiErrorNavigator } from 'src/hooks/navigation/useApiErrorNavigator';
import { ILocation, ILocationListing } from 'src/interfaces';
import { locationGroupAssignLocationEvent } from 'src/utils/google-tag-manager';

import { retainScheduleDaypartOverrides } from '../../listLocationsPage/helpers/scheduleDaypartOverrides.helper';
import {
  addOrReplaceLocationGroupID,
  getViewLocationGroupFromID,
} from '../helpers/AssignLocationGroupPage.helper';

export const useLocationGroupAssignPage = (locationGroupId: string) => {
  const updateLocations = useUpdateLocations();
  const [isLocationAdded, setLocationAdded] = useState<boolean>(false);
  const { data: locationGroup, error } = useGetLocationGroup(locationGroupId);

  const publishModal = useModal();
  const navigate = useApiErrorNavigator(error);

  const publicationCloseModal = () => {
    publishModal.onCloseModal();
    navigate({
      pathname: PATHS.LOCATION_GROUP_TIMINGS(String(locationGroupId)),
    });
  };

  const saveLocation = async (
    selectedLocations: (ILocation | ILocationListing)[],
  ) => {
    const locationids = selectedLocations.map(
      (location) => location.location_code,
    );

    const viewLocationGroupData =
      await fetchViewLocationsByGroupsWithoutPaginationFilterByLocationCode(
        null,
        [`locations.location_code.list=${locationids.join('|')}`],
      );

    const normalizedLocations = selectedLocations.map((location) => {
      const groupIdsFromObjects =
        (location as ILocationListing).location_groups?.map(
          (group) => group.id,
        ) ?? [];
      const mergedIds = Array.from(
        new Set([
          ...(location.location_group_id || []),
          ...groupIdsFromObjects,
        ]),
      );

      return {
        ...location,
        location_group_id: mergedIds,
      };
    });

    const updatedLocations = normalizedLocations.map((location) => ({
      ...location,
      location_group_id: addOrReplaceLocationGroupID(
        location,
        Number(locationGroupId),
        viewLocationGroupData,
      ),
    }));

    // * shared functionality between DetailLocationPage.tsx, UnassignedLocationTab.tsx, AssignLocationGroupPage.hook.ts
    // only unassigned locations/default groups so no deleteScheduleLocationExclusions
    for (const location of updatedLocations) {
      const newLocationGroupView = getViewLocationGroupFromID(
        viewLocationGroupData,
        Number(locationGroupId),
      );

      if (newLocationGroupView) {
        await retainScheduleDaypartOverrides(location.location_code, [
          newLocationGroupView,
        ]);
      }
    }

    await updateLocations.mutateAsync(updatedLocations);

    forEach(updatedLocations, (location) => {
      locationGroupAssignLocationEvent(
        location.location_group_id,
        locationGroup?.channel_name,
      );
    });
    setLocationAdded(true);
    publishModal.onOpenModal();
  };

  return {
    locationGroup,
    publishModal,
    isLocationAdded,
    publicationCloseModal,
    saveLocation,
  };
};
